import { FormRenderProps, Form } from 'react-final-form';
import { useCallback } from 'react';

import { Button } from 'UIPackage/MainModule/components/Button';
import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { Collapse } from 'UIPackage/MainModule/components/Collapse';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';

import { IRenderFormContent } from '../interfaces/IRenderFormContent';
import styles from './FiltersForm.module.scss';
import { FormErrors } from '../../../KnmPackage/MainModule/types/FormErrors';

interface IFiltersFormProps<T> {
  initialValues: T;
  filterName: string;
  handleFormSubmit: (values: T) => void;
  fieldsCounter: (data: T) => void;
  renderContent: IRenderFormContent<T>;
  saveFilter: (data: T) => void;
  setActiveTab: (value: 'main' | 'add') => void;
  setFormValues: (values: T) => void;
  formValidate?: (values: T) => FormErrors<T>;
}

export function FiltersForm<T>({
  initialValues,
  filterName,
  handleFormSubmit,
  fieldsCounter,
  renderContent,
  saveFilter,
  setActiveTab,
  setFormValues,
  formValidate,
}: IFiltersFormProps<T>) {
  const validate = useCallback(
    (values: T) => {
      fieldsCounter(values);

      const validationResult = {
        name: !filterName ? true : undefined,
      };

      if (!formValidate) {
        return validationResult;
      }

      return {
        ...validationResult,
        ...formValidate(values),
      };
    },
    [fieldsCounter, filterName, formValidate],
  );

  const renderForm = useCallback(
    (props: FormRenderProps<T>) => {
      const { values, invalid: isInvalid, handleSubmit } = props;
      const { primaryContent, secondaryContent, onRender } = renderContent(props);

      function handleSaveFilterButton() {
        saveFilter(values);
      }

      function handleFilterAddButtonClick() {
        setFormValues(values);
        setActiveTab('add');
      }

      if (onRender) {
        onRender();
      }

      return (
        <form className={styles.Form} onSubmit={handleSubmit}>
          <div className={styles.FilterBody}>
            <Collapse
              header={
                <div className={styles.Panel}>
                  Основные параметры
                  <IconCollapse className={`${styles.PanelIcon} panel-icon`} />
                </div>
              }
              defaultOpen
            >
              <div className={styles.FilterPanelBody}>{primaryContent}</div>
            </Collapse>
            {secondaryContent && (
              <Collapse
                header={
                  <div className={styles.Panel}>
                    Дополнительные параметры <IconCollapse className={`${styles.PanelIcon} panel-icon`} />
                  </div>
                }
                defaultOpen
              >
                <div className={styles.FilterPanelBody}>
                  {secondaryContent}
                  <Button
                    text="Добавить"
                    type="button"
                    color="primary"
                    variant="link"
                    onClick={handleFilterAddButtonClick}
                  />
                </div>
              </Collapse>
            )}
          </div>
          <div className={styles.FilterFooter}>
            <ModalActions padding="none" placement="left" align="centre" margin="medium">
              <Button text="Применить" color="primary" type="submit" visuallyDisabled={isInvalid} />
              <Button
                text="Сохранить шаблон"
                type="button"
                color="primary"
                variant="link"
                id="saveButton"
                disabled={isInvalid}
                visuallyDisabled={isInvalid}
                onClick={handleSaveFilterButton}
              />
            </ModalActions>
          </div>
        </form>
      );
    },
    [renderContent, saveFilter, setActiveTab, setFormValues],
  );

  return <Form<T> initialValues={initialValues} onSubmit={handleFormSubmit} render={renderForm} validate={validate} />;
}
